"use client";

import Link from "next/link";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface ProductHeroSectionProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  variant?: "default" | "elegant" | "minimal";
}

export default function ProductHeroSection({
  title,
  subtitle,
  breadcrumbs = [{ label: "Home", href: "/" }, { label: title }],
  variant = "default",
}: ProductHeroSectionProps) {
  const variantStyles = {
    default: {
      section: "bg-gradient-to-br from-[#F6FAED] via-white to-[#FAEFFF]",
      title:
        "bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent",
      subtitle: "text-gray-600",
    },
    elegant: {
      section: "bg-gradient-to-b from-slate-50 to-white",
      title: "text-gray-900",
      subtitle: "text-gray-500",
    },
    minimal: {
      section: "bg-white",
      title: "text-gray-900",
      subtitle: "text-gray-600",
    },
  };

  const styles = variantStyles[variant];

  return (
    <section
      className={`relative ${styles.section} py-20 md:py-24 overflow-hidden`}
    >
      {/* Subtle decorative element */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-[#7771B8] rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          {/* Main Title */}
          <h1
            className={`text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight mb-4 ${styles.title} font-[family-name:var(--font-playfair)]`}
          >
            {title}
          </h1>

          {/* Breadcrumb Navigation */}
          <nav
            className="flex justify-center items-center mb-6"
            aria-label="Breadcrumb"
          >
            <ol className="flex items-center space-x-2 text-sm">
              {breadcrumbs.map((item, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <span
                      className="mx-2 text-gray-300 select-none"
                      aria-hidden="true"
                    >
                      /
                    </span>
                  )}
                  {item.href ? (
                    <Link
                      href={item.href}
                      className="text-gray-500 hover:text-green-600 transition-colors duration-200 font-medium"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span className="text-gray-700 font-semibold">
                      {item.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          {/* Optional Subtitle */}
          {subtitle && (
            <p
              className={`text-lg md:text-xl max-w-2xl mx-auto leading-relaxed ${styles.subtitle}`}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
